import { CSSProperties, ReactElement, useEffect, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { Select, Spin } from 'antd'
import uniqBy from 'lodash/uniqBy'

import { getUserOrganizationGroups } from 'api/Cerebro/orgScoped/userApi'
import { CerebroPaginatedResponse } from 'api/Cerebro/types'
import { useCerebroApiRequest } from 'hooks'
import { OrganizationGroup } from 'types'

import {
    buildSerializableOrgGroupLabel,
    SerializableOrgGroup,
    serializeOrgGroup,
} from './serializableOrgGroup'
import SerializableOrgGroupDisplay from './SerializableOrgGroupDisplay'

const SEARCH_RESULTS_PER_QUERY = 100

interface SelectOption {
    label: ReactElement | string
    value?: string
    locked?: boolean
    searchable?: string
}

interface OrganizationGroupSearchSelectProps {
    orgId: string
    selectedOrgGroupId: string | undefined
    onChange: (
        orgGroupId: string | undefined,
        value: SerializableOrgGroup | undefined
    ) => void
    className?: string
    style?: CSSProperties
}

const OrganizationGroupSearchSelect = ({
    orgId,
    selectedOrgGroupId,
    onChange,
    className,
    style,
}: OrganizationGroupSearchSelectProps): ReactElement => {
    const makeCerebroApiRequest = useCerebroApiRequest({ throwOnError: true })
    const { data: organizationGroupsResponse, isLoading } = useQuery({
        queryKey: ['organizationGroups', orgId],
        queryFn: () =>
            makeCerebroApiRequest<
                unknown,
                CerebroPaginatedResponse<OrganizationGroup>
            >({
                request: getUserOrganizationGroups({
                    limit: SEARCH_RESULTS_PER_QUERY,
                    organization: orgId,
                    ordering: 'name',
                }),
            }),
    })

    const options = useMemo(
        () =>
            (organizationGroupsResponse?.data.results ?? []).map((option) => {
                const {
                    id: organizationGroupId,
                    name: organizationGroupName,
                    organization: {
                        id: organizationId,
                        name: organizationName,
                        locked,
                    },
                    agency,
                } = option
                const serializable: SerializableOrgGroup = {
                    orgId: organizationId,
                    orgGroupId: organizationGroupId,
                    locked,
                    label: buildSerializableOrgGroupLabel(
                        organizationName,
                        organizationGroupName,
                        agency?.name
                    ),
                }
                // Return a minimal object that complies with Typeahead types and has everything we need
                return {
                    value: serializeOrgGroup(serializable),
                    label: `${organizationId} ${organizationName}`,
                    serializable,
                }
            }),
        [organizationGroupsResponse?.data.results]
    )

    useEffect(() => {
        if (!selectedOrgGroupId && options?.length) {
            let defaultGroup = options.find((o) =>
                o.serializable.label?.toLowerCase().includes('admin')
            )
            if (!defaultGroup) {
                defaultGroup = options[0]
            }
            onChange(
                defaultGroup.serializable.orgGroupId,
                defaultGroup.serializable
            )
        }
    }, [selectedOrgGroupId, options, onChange])

    // Construct the proper objects to be used in the select
    const searchOptions: SelectOption[] = options.map((option) => {
        return {
            value: option.serializable.orgGroupId,
            label: (
                <SerializableOrgGroupDisplay
                    showOrg={false}
                    serializable={option.serializable}
                />
            ),
            locked: option.serializable.locked ?? false,
            searchable: option.label,
        }
    })

    const allOptions = uniqBy([...searchOptions], 'value')

    function handleChange(orgGroupId: string | undefined): void {
        const match = options.find(
            (option) => option.serializable.orgGroupId === orgGroupId
        )
        onChange(orgGroupId, match?.serializable)
    }

    return (
        <Select<string, SelectOption>
            onKeyDown={(e) => {
                // Prevent Menu from handling arrow keys
                if (
                    [
                        'ArrowUp',
                        'ArrowDown',
                        'ArrowLeft',
                        'ArrowRight',
                    ].includes(e.key)
                ) {
                    e.stopPropagation()
                }
            }}
            className={className}
            style={{ ...style, width: '100%' }}
            optionFilterProp="searchable"
            optionLabelProp="label"
            placeholder="Select an Organization Group"
            loading={isLoading}
            notFoundContent={isLoading ? <Spin size="small" /> : null}
            onChange={handleChange}
            value={selectedOrgGroupId}
            options={allOptions}
        />
    )
}

export default OrganizationGroupSearchSelect
